// src/components/BaslikTemplateEditor/BaslikTemplateEditor.jsx
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './BaslikTemplateEditor.css';

import {
  selectEditorState,
  closeEditor,
  setEditorDxf,
  selectEntity,
  bindVariable,
  unbindVariable,
  setTemplateName,
  saveTemplate,
} from '../../redux/baslikTemplateSlice';

import { VARIABLE_CATALOG } from '../../config/baslikConfig/variableCatalog';
import { useTemplatePreviewScene } from '../../hooks/useTemplatePreviewScene';
import { useTemplateDxfLoader } from '../../hooks/useTemplateDxfLoader';
import { useTemplateTextSelection } from '../../modules/selection';
import { dxfDrawCore } from '../../modules/dxf';

// Baslik sablonlari icin max dosya boyutu (2 MB)
const MAX_DXF_SIZE = 2 * 1024 * 1024;

const BaslikTemplateEditor = () => {
  const dispatch = useDispatch();
  const editorState = useSelector(selectEditorState);
  const {
    dxfRawText,
    textEntities,
    selectedEntityKey,
    pendingBindings,
    templateName,
  } = editorState;

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Preview sahnesi
  const previewContainerRef = useRef(null);
  const { scene: sceneRef, camera: cameraRef, renderer: rendererRef, clearScene, fitToContent } =
    useTemplatePreviewScene(previewContainerRef);

  // DXF loader
  const { loadTemplateDxf } = useTemplateDxfLoader();
  const fileInputRef = useRef(null);

  // Text secimi
  const { handleClick: handleTextClick } = useTemplateTextSelection(
    sceneRef, cameraRef, rendererRef, textEntities
  );

  // Akordion state
  const [openCategories, setOpenCategories] = useState(() => {
    const map = {};
    VARIABLE_CATALOG.forEach(cat => { map[cat.category] = true; });
    return map;
  });

  // Parsed DXF data ref (THREE sahnesine render etmek icin)
  const parsedDxfRef = useRef(null);
  // Render guard - ayni dxfRawText icin tekrar render etmeyi onle
  const lastRenderedRef = useRef(null);

  // DXF yuklendiginde preview sahnesinde render et
  useEffect(() => {
    if (!dxfRawText || !parsedDxfRef.current || !sceneRef.current) return;
    // Ayni icerik icin tekrar render etme
    if (lastRenderedRef.current === dxfRawText) return;
    lastRenderedRef.current = dxfRawText;

    const { dxfData, layers } = parsedDxfRef.current;

    clearScene();

    try {
      // dxfDrawCore ile render - target="scene" olmali!
      const entities = dxfData.entities || [];
      const blocks = dxfData.blocks || {};
      dxfDrawCore(sceneRef.current, entities, blocks, "scene", layers);

      // Text entity'lere entityKey userData ekle
      let textIdx = 0;
      let mtextIdx = 0;
      sceneRef.current.traverse((obj) => {
        if (obj.userData) {
          if (obj.userData.entityType === 'TEXT' || obj.userData.type === 'text') {
            obj.userData.entityKey = `text_${textIdx++}`;
          } else if (obj.userData.entityType === 'MTEXT' || obj.userData.type === 'mtext') {
            obj.userData.entityKey = `mtext_${mtextIdx++}`;
          }
        }
      });
    } catch (err) {
      console.error('[BaslikTemplateEditor] DXF render hatasi:', err);
    }

    // Kamerayi sigdir - setTimeout ile layout'un oturmesini bekle
    setTimeout(() => fitToContent(), 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dxfRawText]);

  // ─── Handlers ──────────────────────────────────────
  const handleFileUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Dosya boyutu kontrolu
    if (file.size > MAX_DXF_SIZE) {
      setLoadError(`Dosya cok buyuk (${(file.size / 1024 / 1024).toFixed(1)} MB). Baslik sablonlari icin max ${MAX_DXF_SIZE / 1024 / 1024} MB.`);
      e.target.value = '';
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    lastRenderedRef.current = null; // Yeni dosya icin render guard'i sifirla

    // setTimeout ile UI'in "Yükleniyor..." gostermesini sagla
    setTimeout(() => {
      loadTemplateDxf(file, (result) => {
        parsedDxfRef.current = result;
        dispatch(setEditorDxf({
          dxfRawText: result.dxfRawText,
          textEntities: result.textEntities,
        }));
        setIsLoading(false);
      }, (err) => {
        console.error('[BaslikTemplateEditor] DXF yuklenemedi:', err);
        setLoadError('DXF dosyasi okunamadi: ' + (err.message || 'Bilinmeyen hata'));
        setIsLoading(false);
      });
    }, 50);

    // Input'u sifirla (ayni dosya tekrar secilirse tetiklensin)
    e.target.value = '';
  }, [dispatch, loadTemplateDxf]);

  const handlePreviewClick = useCallback((e) => {
    if (!dxfRawText || isLoading) return;
    handleTextClick(e);
  }, [dxfRawText, isLoading, handleTextClick]);

  const handleClose = useCallback(() => {
    parsedDxfRef.current = null;
    lastRenderedRef.current = null;
    dispatch(closeEditor());
  }, [dispatch]);

  const handleSave = useCallback(() => {
    dispatch(saveTemplate());
  }, [dispatch]);

  const handleBindVariable = useCallback((variablePath) => {
    if (!selectedEntityKey) return;
    const entity = textEntities.find(te => te.key === selectedEntityKey);
    if (!entity) return;
    dispatch(bindVariable({
      entityKey: selectedEntityKey,
      variablePath,
      originalText: entity.text,
      entityType: entity.type,
    }));
  }, [dispatch, selectedEntityKey, textEntities]);

  const handleUnbind = useCallback((entityKey) => {
    dispatch(unbindVariable(entityKey));
  }, [dispatch]);

  const toggleCategory = useCallback((cat) => {
    setOpenCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  }, []);

  // Secili text entity bilgisi
  const selectedEntity = selectedEntityKey
    ? textEntities.find(te => te.key === selectedEntityKey) : null;
  const selectedBinding = selectedEntityKey
    ? pendingBindings[selectedEntityKey] : null;

  // Kaydetme aktiflik kontrolu
  const canSave = !!dxfRawText && !!templateName.trim();

  // Halihazirda baglanmis variable path'ler
  const boundPaths = new Set(Object.values(pendingBindings).map(b => b.variablePath));

  return (
    <div className="bte-overlay" onClick={handleClose}>
      <div className="bte-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bte-header">
          <h2>Baslik Sablon Editoru</h2>
          <button className="bte-close-btn" onClick={handleClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="bte-body">
          {/* Sol: Preview */}
          <div className="bte-preview-panel">
            <div
              className="bte-preview-container"
              ref={previewContainerRef}
              onClick={handlePreviewClick}
            >
              {!dxfRawText && !isLoading && (
                <div className="bte-preview-empty">
                  DXF dosyasi yukleyin
                </div>
              )}
              {isLoading && (
                <div className="bte-preview-empty">
                  Yükleniyor...
                </div>
              )}
            </div>
            <div className="bte-upload-bar">
              <button className="bte-upload-btn" onClick={handleFileUpload} disabled={isLoading}>
                {isLoading ? 'Yükleniyor...' : 'DXF Yükle'}
              </button>
              <input
                ref={fileInputRef}
                className="bte-dxf-input"
                type="file"
                accept=".dxf"
                onChange={handleFileChange}
              />
              {loadError && (
                <span style={{ fontSize: 11, color: '#f44', alignSelf: 'center' }}>
                  {loadError}
                </span>
              )}
              {dxfRawText && !isLoading && (
                <span style={{ fontSize: 11, color: '#888', alignSelf: 'center' }}>
                  {textEntities.length} text alani bulundu
                </span>
              )}
            </div>
          </div>

          {/* Sag: Sidebar */}
          <div className="bte-sidebar">
            {/* Sablon Adi */}
            <div className="bte-name-section">
              <label>Sablon Adi</label>
              <input
                className="bte-name-input"
                type="text"
                value={templateName}
                onChange={e => dispatch(setTemplateName(e.target.value))}
                placeholder="Ornek: Belediye Sablonu"
              />
            </div>

            {/* Secili Text */}
            <div className="bte-selected-section">
              <div className="bte-section-title">Secili Text</div>
              {!selectedEntity ? (
                <div className="bte-selected-none">
                  Preview'da bir text'e tiklayin
                </div>
              ) : (
                <>
                  <div className="bte-selected-key">{selectedEntity.key} ({selectedEntity.type})</div>
                  <div className="bte-selected-text">"{selectedEntity.text}"</div>
                  {selectedBinding ? (
                    <>
                      <div className="bte-selected-bound">
                        Bagli: {selectedBinding.variablePath}
                      </div>
                      <button
                        className="bte-unbind-btn"
                        onClick={() => handleUnbind(selectedEntityKey)}
                      >
                        Baglamayi Kaldir
                      </button>
                    </>
                  ) : (
                    <div className="bte-selected-none">
                      Aşağıdan bir değişken seçin
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Degisken Katalogu */}
            <div className="bte-catalog-section">
              <div className="bte-section-title">Degisken Katalogu</div>
              {VARIABLE_CATALOG.map(cat => {
                const isOpen = openCategories[cat.category] !== false;
                return (
                  <div key={cat.category}>
                    <div
                      className="bte-catalog-category"
                      onClick={() => toggleCategory(cat.category)}
                    >
                      <span className={`bte-catalog-chevron ${isOpen ? 'open' : ''}`}>
                        &#9654;
                      </span>
                      {cat.category}
                    </div>
                    <div className={`bte-catalog-items ${isOpen ? '' : 'collapsed'}`}>
                      {cat.variables.map(v => (
                        <div
                          key={v.path}
                          className={`bte-catalog-item ${
                            !selectedEntityKey || boundPaths.has(v.path) ? 'disabled' : ''
                          }`}
                          onClick={() => handleBindVariable(v.path)}
                          title={v.path}
                        >
                          {v.label}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Baglama Listesi */}
            <div className="bte-bindings-section">
              <div className="bte-section-title">Baglamalar</div>
              {Object.keys(pendingBindings).length === 0 ? (
                <div className="bte-no-bindings">Henuz baglama yok</div>
              ) : (
                Object.entries(pendingBindings).map(([key, binding]) => (
                  <div key={key} className="bte-binding-row">
                    <span className="bte-binding-key">{key}</span>
                    <span className="bte-binding-arrow">&rarr;</span>
                    <span className="bte-binding-path">{binding.variablePath}</span>
                    <button
                      className="bte-binding-remove"
                      onClick={() => handleUnbind(key)}
                      title="Kaldir"
                    >
                      &times;
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bte-footer">
          <button className="bte-cancel-btn" onClick={handleClose}>İptal</button>
          <button
            className="bte-save-btn"
            onClick={handleSave}
            disabled={!canSave}
          >
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};

export default BaslikTemplateEditor;
